import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Heart, Flame, Footprints, Moon, Activity, X, Play, Pause, RotateCcw, CheckCircle2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import type { ITrainingPlanDay } from '@/data/training-plan-generator';

interface AerobicDetailSheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  day: ITrainingPlanDay | null;
  /** 降量百分比（0-100），0 表示不降量 */
  reductionPercent?: number;
  /** 异常指标数量，用于降量提示文案 */
  abnormalCount?: number;
  /** 是否今日建议休息 */
  shouldRest?: boolean;
  /** 是否为今日（只有今日才应用降量/休息建议 & 显示完成按钮） */
  isToday?: boolean;
  /** 今日是否已完成有氧 */
  isCompleted?: boolean;
  /** 标记今日有氧已完成的回调 */
  onMarkComplete?: () => void;
}

const TYPE_META: Record<string, { icon: typeof Heart; iconBg: string; iconColor: string }> = {
  cardio: { icon: Heart, iconBg: 'bg-warning/15', iconColor: 'text-warning' },
  hiit: { icon: Flame, iconBg: 'bg-warning/15', iconColor: 'text-warning' },
  'active-rest': { icon: Footprints, iconBg: 'bg-muted', iconColor: 'text-muted-foreground' },
  rest: { icon: Moon, iconBg: 'bg-muted', iconColor: 'text-muted-foreground' },
};

function formatTime(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * 有氧/HIIT/主动恢复/休息日 详情弹窗
 * - 未开始：显示详情 + 「开始训练」主按钮 + 「我已完成」快捷完成
 * - 进行中：显示计时器 + 「暂停/继续」+ 「结束并保存」
 * - 已完成：显示完成状态
 */
export default function AerobicDetailSheet({
  open,
  onOpenChange,
  day,
  reductionPercent = 0,
  abnormalCount = 0,
  shouldRest = false,
  isToday = false,
  isCompleted = false,
  onMarkComplete,
}: AerobicDetailSheetProps) {
  const meta = day ? TYPE_META[day.type] || TYPE_META.rest : TYPE_META.rest;
  const Icon = meta.icon;

  // 计时内部状态（每次打开弹窗重置，不持久化到 storage）
  const [elapsedSec, setElapsedSec] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef<number | null>(null);

  const isReduced = useMemo(() => {
    if (!day) return false;
    if (day.type === 'rest') return false;
    if (reductionPercent <= 0) return false;
    return isToday;
  }, [day, reductionPercent, isToday]);

  const showRestWarning = useMemo(() => {
    if (!day) return false;
    if (day.type === 'rest') return false;
    return isToday && shouldRest;
  }, [day, isToday, shouldRest]);

  const adjustedDuration = useMemo(() => {
    if (!day) return 0;
    if (!isReduced) return day.durationMin;
    return Math.max(10, Math.round(day.durationMin * (1 - reductionPercent / 100)));
  }, [day, isReduced, reductionPercent]);

  const targetSec = adjustedDuration * 60;

  // 进度百分比
  const progressPct = useMemo(() => {
    if (targetSec <= 0) return 0;
    return Math.min(100, Math.round((elapsedSec / targetSec) * 100));
  }, [elapsedSec, targetSec]);

  // 计时器
  useEffect(() => {
    if (isRunning) {
      timerRef.current = window.setInterval(() => {
        setElapsedSec(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning]);

  // 弹窗关闭时重置计时（下次打开从头开始）
  useEffect(() => {
    if (!open) {
      setIsRunning(false);
      setElapsedSec(0);
    }
  }, [open]);

  const handleStart = useCallback(() => {
    setIsRunning(true);
    toast('有氧训练已开始，加油！');
  }, []);

  const handlePauseResume = useCallback(() => {
    setIsRunning(prev => !prev);
  }, []);

  const handleReset = useCallback(() => {
    setIsRunning(false);
    setElapsedSec(0);
  }, []);

  const handleFinish = useCallback(() => {
    setIsRunning(false);
    onMarkComplete?.();
    toast.success(`已完成 ${Math.floor(elapsedSec / 60)} 分钟有氧训练`);
    onOpenChange?.(false);
  }, [elapsedSec, onMarkComplete, onOpenChange]);

  const handleQuickComplete = useCallback(() => {
    onMarkComplete?.();
    toast.success('已标记今日有氧训练完成');
    onOpenChange?.(false);
  }, [onMarkComplete, onOpenChange]);

  // 休息日不弹
  if (day?.type === 'rest') return null;

  const showTraining = isToday && !isCompleted && !shouldRest;
  const hasStarted = elapsedSec > 0 || isRunning;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 border-0 rounded-t-3xl metallic-card max-h-[85vh] overflow-y-auto"
      >
        {day && (
          <div className="p-5 pb-[calc(20px+env(safe-area-inset-bottom)+20px)]">
            <SheetHeader className="mb-4 text-left">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${meta.iconBg}`}>
                  <Icon className={`w-6 h-6 ${meta.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-base font-bold text-foreground">
                    {day.label}
                  </SheetTitle>
                  <SheetDescription className="text-xs text-muted-foreground mt-0.5">
                    {day.subtitle}
                  </SheetDescription>
                </div>
                {isCompleted && (
                  <div className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-success/15 text-success">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-bold">已完成</span>
                  </div>
                )}
              </div>
            </SheetHeader>

            {/* 计时器区域（今日 & 未完成 & 非建议休息时显示） */}
            {showTraining && (
              <div className="mb-5 rounded-2xl bg-card/60 border border-border/40 p-5 text-center">
                <div className="text-[11px] text-muted-foreground mb-1">
                  {hasStarted ? '已训练时长' : '目标时长'}
                </div>
                <div className="text-4xl font-black tabular-nums text-foreground tracking-tight">
                  {formatTime(hasStarted ? elapsedSec : targetSec)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {hasStarted ? `目标 ${adjustedDuration} 分钟` : `约 ${adjustedDuration} 分钟`}
                </div>

                {/* 进度条 */}
                {hasStarted && (
                  <div className="mt-3 h-2 rounded-full bg-border/40 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-warning to-warning/70 transition-all duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                )}

                {/* 控制按钮 */}
                <div className="flex items-center justify-center gap-3 mt-4">
                  {!hasStarted ? (
                    <button
                      onClick={handleStart}
                      className="btn-primary-gradient px-8 py-3 rounded-xl text-sm font-bold text-white flex items-center gap-2"
                    >
                      <Play className="w-4 h-4 fill-current" />
                      开始训练
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleReset}
                        className="w-11 h-11 rounded-full bg-muted flex items-center justify-center active:scale-95 transition-transform"
                        aria-label="重置"
                      >
                        <RotateCcw className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={handlePauseResume}
                        className="w-14 h-14 rounded-full btn-primary-gradient flex items-center justify-center active:scale-95 transition-transform"
                        aria-label={isRunning ? '暂停' : '继续'}
                      >
                        {isRunning ? (
                          <Pause className="w-5 h-5 text-white fill-current" />
                        ) : (
                          <Play className="w-5 h-5 text-white fill-current" />
                        )}
                      </button>
                      <button
                        onClick={handleFinish}
                        className="w-11 h-11 rounded-full bg-success/15 flex items-center justify-center active:scale-95 transition-transform"
                        aria-label="结束并保存"
                      >
                        <CheckCircle2 className="w-5 h-5 text-success" />
                      </button>
                    </>
                  )}
                </div>
                {hasStarted && (
                  <p className="text-[10px] text-muted-foreground mt-3">
                    点击 ✓ 可提前结束并记录完成
                  </p>
                )}
              </div>
            )}

            {/* 详情信息 */}
            <div className="space-y-2 mb-5">
              {/* 时长 */}
              <div className="flex items-center justify-between py-2 border-b border-border/30">
                <span className="text-xs text-muted-foreground">时长</span>
                <div className="text-right">
                  {isReduced ? (
                    <>
                      <span className="text-xs line-through opacity-60 text-muted-foreground block">
                        {day.durationMin} 分钟
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {adjustedDuration} 分钟
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-semibold text-foreground">
                      {day.durationMin} 分钟
                    </span>
                  )}
                </div>
              </div>

              {/* 降量提示条 */}
              {isReduced && (
                <div className="flex items-center gap-1.5 py-2 px-3 rounded-lg bg-warning/10">
                  <Activity className="w-3.5 h-3.5 text-warning shrink-0" />
                  <span className="text-[11px] text-warning font-medium">
                    已自动降量 {reductionPercent}%（{abnormalCount} 项指标异常）
                  </span>
                </div>
              )}

              {/* 建议休息提示条 */}
              {showRestWarning && (
                <div className="flex items-center gap-1.5 py-2 px-3 rounded-lg bg-destructive/10">
                  <X className="w-3.5 h-3.5 text-destructive shrink-0" />
                  <span className="text-[11px] text-destructive font-medium">
                    今日建议休息（{abnormalCount} 项异常），训练已顺延
                  </span>
                </div>
              )}

              {/* 强度 */}
              <div className="flex items-center justify-between py-2 border-b border-border/30">
                <span className="text-xs text-muted-foreground">强度</span>
                <span className="text-sm font-semibold text-foreground">
                  {day.intensity || '低'}
                </span>
              </div>

              {/* 有氧项目（如有） */}
              {day.cardioItem && (
                <div className="flex items-center justify-between py-2 border-b border-border/30">
                  <span className="text-xs text-muted-foreground">推荐项目</span>
                  <span className="text-sm font-semibold text-foreground">
                    {day.cardioItem}
                  </span>
                </div>
              )}

              {/* 说明 */}
              {day.note && (
                <div className="pt-2">
                  <span className="text-xs text-muted-foreground">说明</span>
                  <p className="text-sm text-foreground mt-1">{day.note}</p>
                </div>
              )}
            </div>

            {/* 底部按钮区 */}
            {isToday && !shouldRest ? (
              isCompleted ? (
                <div className="flex items-center justify-center gap-2 py-3 rounded-xl bg-success/10 text-success">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-bold">今日有氧训练已完成</span>
                </div>
              ) : hasStarted ? (
                <button
                  onClick={handleFinish}
                  className="w-full py-3 rounded-xl bg-success/15 text-success text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  结束训练并保存
                </button>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={handleStart}
                    className="w-full py-3 rounded-xl btn-primary-gradient text-white text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                  >
                    <Play className="w-4 h-4 fill-current" />
                    开始训练（计时）
                  </button>
                  <button
                    onClick={handleQuickComplete}
                    className="w-full py-3 rounded-xl bg-muted text-foreground text-sm font-bold active:scale-[0.98] transition-transform"
                  >
                    我已在外面练完，标记完成
                  </button>
                </div>
              )
            ) : (
              <button
                onClick={() => onOpenChange?.(false)}
                className="w-full py-3 rounded-xl bg-muted text-foreground text-sm font-bold active:scale-[0.98] transition-transform"
              >
                我知道了
              </button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
